'use client';

import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, ShieldCheck } from 'lucide-react';

import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';
import { Eyebrow } from '@/components/custom/section-shell';
import { Reveal } from '@/components/custom/reveal';

const features = [
  'Clientes ilimitados',
  'Reparaciones ilimitadas',
  'Usuarios ilimitados',
  'Presupuestos y plantillas',
  'Estados de seguimiento',
  'Localización de equipos',
  'Gestión de compañías',
  'Hasta 10 fotos por reparación',
  'Notas y comentarios',
  'Soporte 24 hs',
];

const Pricing = () => {
  // The benefits cascade needs its OWN trigger: keyed off the card-level Reveal
  // it fired while the list was still below the fold (the card is tall and lives
  // in the sticky slide-over), so the animation ran off-screen. This observer
  // watches the list itself and only fires when it's actually visible.
  const listRef = useRef<HTMLUListElement>(null);
  const [phase, setPhase] = useState<'idle' | 'armed' | 'in'>('idle');

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = listRef.current;

    if (!el) return;

    setPhase('armed');

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPhase('in');
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    io.observe(el);

    return () => io.disconnect();
  }, []);

  return (
    <section id="precio" className="bg-secondary py-[var(--section-y-sm)] md:py-[var(--section-y)]">
      <div className="mx-auto w-full max-w-[var(--container)] px-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <Eyebrow>Precio</Eyebrow>
          <h2 className="max-w-2xl font-display text-h2 font-bold tracking-[-0.02em] text-ink">
            Un plan. Cero complicaciones.
          </h2>
          <p className="max-w-xl text-lead text-ink-soft">
            Todo Mendly por un precio fijo mensual. Probalo 30 días gratis, sin tarjeta.
          </p>
        </div>

        <Reveal className="mx-auto mt-12 max-w-lg overflow-hidden rounded-2xl border border-brand/40 bg-surface shadow-[var(--shadow-lg)] ring-1 ring-brand/10">
          <div className="flex flex-col items-center gap-2 bg-brand-solid px-8 pt-8 pb-6 text-center text-white">
            <span className="rounded-full bg-white/15 px-3 py-1 font-mono text-eyebrow uppercase tracking-[0.18em] text-white">
              30 días gratis
            </span>
            <span className="mt-1 font-display text-h3 font-semibold text-white">Plan Único</span>
            <div className="flex items-end gap-1">
              <span className="font-display text-5xl font-bold tracking-[-0.02em] text-white">
                $39.999
              </span>
              <span className="mb-1.5 text-white/70">/mes</span>
            </div>
            <p className="text-white/80">Todo lo que necesitás, sin límites por módulo.</p>
          </div>

          <div className="px-8 py-9 sm:px-10">
            <ul
              ref={listRef}
              className={cn(
                'grid grid-cols-1 gap-x-8 gap-y-3.5 sm:grid-cols-2',
                phase === 'armed' && 'benefits-armed',
                phase === 'in' && 'benefits-in',
              )}
            >
              {features.map((f, i) => (
                <li
                  key={f}
                  className="benefit-item flex items-start gap-2.5 text-ink"
                  style={{ animationDelay: `${i * 0.05}s` }}
                >
                  <Check className="mt-0.5 size-4 shrink-0 text-green" />
                  {f}
                </li>
              ))}
            </ul>

            <a
              href="https://backoffice.mendly.com.ar/signup"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('trial_started', { location: 'pricing' })}
              className="group mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-md bg-brand font-medium text-brand-ink shadow-[var(--shadow-md)] transition-colors hover:bg-brand/90"
            >
              Empezá ahora
              <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
            </a>

            <p className="mt-4 flex items-center justify-center gap-2 text-sm text-ink-soft">
              <ShieldCheck className="size-4 text-green" />
              Sin tarjeta · Cancelás cuando quieras
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
